import { ReactNode } from 'react';
import { Checkbox } from 'antd';
import styled from 'styled-components';

import { Form } from '@components/Form';
import { useClientEnvironment } from '@hooks/clientEnvironment';

import { useSignupPageContext } from '../../SignupPageContext';

export const LegalChecks = (): JSX.Element => {
  const { isUkSelected } = useSignupPageContext();

  const { data: clientEnvironment } = useClientEnvironment();
  const { 
    identifier,
    privacy_policy_domain_uk,
    privacy_policy_domain_us,
    terms_of_service_domain_uk,
    terms_of_service_domain_us,
  } = clientEnvironment || {};

  const isAlessiHartigan = identifier === 'ah-casting-saas';

  const privacyPolicyLink = isUkSelected ? privacy_policy_domain_uk : privacy_policy_domain_us;
  const termsOfServiceLink = isUkSelected ? terms_of_service_domain_uk : terms_of_service_domain_us;

  const checkList = isUkSelected
    ? getLegalChecksUK(isAlessiHartigan, privacyPolicyLink, termsOfServiceLink)
    : getLegalChecksUSA(
        isAlessiHartigan,
        privacyPolicyLink,
        termsOfServiceLink
      );

  return (
    <StyledContainer>
      {checkList.map(({ name, content }, index) => (
        <CheckboxFormItem
          key={`signup-legal-check-${index}`}
          name={name}
          content={content}
        />
      ))}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

type CheckboxFormItemProps = {
  name: string;
  content: ReactNode;
};
const CheckboxFormItem = ({ name, content }: CheckboxFormItemProps) => (
  <StyledCheckboxFormItem
    name={name}
    valuePropName='checked'
    initialValue={false}
    rules={[
      {
        required: true,
        type: 'boolean',
        message: 'You must agree to the terms to sign up!',

        validator: (rules, checked) => {
          if (!checked) {
            throw new Error();
          }
        },
      },
    ]}
  >
    <Checkbox>{content}</Checkbox>
  </StyledCheckboxFormItem>
);

const StyledLink = styled.a`
  font-size: 12px;
  color: ${(props) => props.theme?.palette?.link?.[0]};
`;

const StyledCheckboxFormItem = styled(Form.Item)`
  &.ant-form-item {
    margin: 0;

    .ant-form-item-explain-error {
      margin: 4px 0 0;
    }

    .ant-form-item-control-input {
      min-height: unset;
    }

    .ant-checkbox-wrapper {
      width: 100%;
      display: flex;
      gap: 5px;
      align-items: center;

      ::after {
        height: 16px;
      }

      span {
        padding: 0;
        font-size: 12px;
        line-height: 16px;
      }

      .ant-checkbox {
        top: 0;
        width: 16px;
        height: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        .ant-checkbox-inner {
          width: 14px;
          height: 14px;
          border-radius: 2px;
          transform: scale(1.125);
        }
      }
    }
  }
`;

const getLegalChecksUSA = (
  isAlessiHartigan: boolean,
  privacyPolicyLink: string,
  termsOfServiceLink: string
): CheckboxFormItemProps[] => {
  return [
    {
      name: 'eligibility',
      content: 'I am legally eligible to work in the United States',
    },
    {
      name: 'terms',
      content: (
        <div>
          I agree to the{' '}
          <StyledLink
            href={termsOfServiceLink}
            target='_blank'
            rel='noreferrer'
          >
            {isAlessiHartigan ? 'Terms of Use' : 'Terms of Service'}
          </StyledLink>{' '}
          and have read and acknowledge our{' '}
          <StyledLink href={privacyPolicyLink} target='_blank' rel='noreferrer'>
            Privacy Policy
          </StyledLink>
        </div>
      ),
    },
  ];
};

const getLegalChecksUK = (
  isAlessiHartigan: boolean,
  privacyPolicyLink: string,
  termsOfServiceLink: string
): CheckboxFormItemProps[] => {
  return [
    {
      name: 'eligibility',
      content: 'I am legally allowed to work in the UK',
    },
    {
      name: 'insurance-number',
      content: 'I have a National Insurance Number',
    },
    {
      name: 'age',
      content: 'I am over the age of 16 (GCSE completed)',
    },
    {
      name: 'terms',
      content: (
        <div>
          I confirm I have read and accept the{' '}
          <StyledLink href={privacyPolicyLink} target='_blank' rel='noreferrer'>
            Privacy Policy
          </StyledLink>{' '}
          and{' '}
          <StyledLink
            href={termsOfServiceLink}
            target='_blank'
            rel='noreferrer'
          >
            {isAlessiHartigan ? 'Terms of Use' : 'Terms of Service'}
          </StyledLink>
        </div>
      ),
    },
  ];
};
